import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';

interface AudioRecorderProps {
  photoId: string;
  onRecordingComplete?: () => void;
  onCancel?: () => void;
}

interface AudioMessage {
  messageId: string;
  authorId: string;
  authorName: string;
  audioUrl: string;
  durationSeconds: number;
  createdAt: string;
}

export default function AudioRecorder({ photoId, onRecordingComplete, onCancel }: AudioRecorderProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const MAX_RECORDING_TIME = 20; // seconds

  // Fetch existing messages to check if user already has one
  const { data: messagesData } = useQuery({
    queryKey: ['messages', photoId],
    queryFn: () => api.listMessages(photoId),
  });

  const messages: AudioMessage[] = messagesData?.messages || [];
  const userExistingMessage = messages.find(msg => msg.authorId === user?.userId);

  // Delete mutation for existing message
  const deleteMutation = useMutation({
    mutationFn: (messageId: string) => api.deleteMessage(photoId, messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', photoId] });
      setShowDeleteConfirm(false);
      setErrorMessage(null);
    },
    onError: (error: unknown) => {
      console.error('Error deleting message:', error);
      setErrorMessage('Failed to delete existing message. Please try again.');
    },
  });

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    setErrorMessage(null); // Clear any previous errors
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= MAX_RECORDING_TIME) {
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return newTime;
        });
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setErrorMessage('Could not access microphone. Please check your browser permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const uploadAudio = async () => {
    if (!audioBlob) return;

    setIsUploading(true);
    setErrorMessage(null);
    try {
      // Create message intent
      const intent = await api.createMessage(photoId, {
        durationSeconds: recordingTime,
      });

      // Upload audio to S3 (convert Blob to File)
      const audioFile = new File([audioBlob], 'audio.webm', { type: 'audio/webm' });
      await api.uploadToS3(intent.uploadUrl, audioFile);

      // Invalidate queries to refresh the messages list
      queryClient.invalidateQueries({ queryKey: ['messages', photoId] });
      
      // Reset state
      setAudioBlob(null);
      setRecordingTime(0);
      setErrorMessage(null);
      
      if (onRecordingComplete) {
        onRecordingComplete();
      }
    } catch (error: unknown) {
      console.error('Error uploading audio:', error);
      
      // Extract error message from API response
      let message = 'Failed to upload audio message. Please try again.';
      if (error && typeof error === 'object') {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        if (err.response?.data?.message) {
          message = err.response.data.message;
        } else if (err.message) {
          message = err.message;
        }
      }
      
      setErrorMessage(message);
    } finally {
      setIsUploading(false);
    }
  };

  const discardRecording = () => {
    setAudioBlob(null);
    setRecordingTime(0);
    chunksRef.current = [];
  };

  const handleCancel = () => {
    if (isRecording) {
      stopRecording();
    }
    discardRecording();
    if (onCancel) {
      onCancel();
    }
  };

  // Show warning if user already has a message
  if (userExistingMessage && !audioBlob && !isRecording) {
    return (
      <div className="space-y-4 p-4 sm:p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border-2 border-amber-200">
        <div className="text-center">
          <h3 className="text-lg font-bold text-gray-900 mb-2">
            You Already Have an Audio Message
          </h3>
          <p className="text-sm text-gray-600">
            You can only have one audio message per photo. Delete your existing message to record a new one.
          </p>
        </div>

        {/* Existing Message Info */}
        <div className="bg-white rounded-lg p-4 border border-amber-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-gray-900">Your Current Message</p>
              <p className="text-xs text-gray-500">
                {Math.floor(userExistingMessage.durationSeconds)}s · {new Date(userExistingMessage.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <audio
            src={userExistingMessage.audioUrl}
            controls
            className="w-full h-8"
          />
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 animate-scale-in">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-900 mb-1">Error</h4>
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
              <button
                onClick={() => setErrorMessage(null)}
                className="text-red-400 hover:text-red-600 transition-colors"
                title="Dismiss"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleteMutation.isPending}
            className="btn-danger px-6 py-3 text-sm disabled:opacity-50"
          >
            {deleteMutation.isPending ? 'Deleting...' : '🗑️ Delete & Record New'}
          </button>
          <button
            onClick={onCancel}
            className="btn-ghost px-6 py-3 text-sm"
          >
            Cancel
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Delete Audio Message?
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete your existing audio message? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-ghost px-4 py-2 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate(userExistingMessage.messageId)}
                  disabled={deleteMutation.isPending}
                  className="btn-danger px-4 py-2 text-sm"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
      <div className="text-center">
        <h3 className="text-lg font-bold text-gray-900 mb-2">
          {audioBlob ? 'Review Your Message' : 'Record New Message'}
        </h3>
        <p className="text-sm text-gray-600">
          {audioBlob 
            ? 'Listen to your recording and save it or record again'
            : `Maximum ${MAX_RECORDING_TIME} seconds per message`
          }
        </p>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 animate-scale-in">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 mb-1">Error</h4>
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-red-400 hover:text-red-600 transition-colors"
              title="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Recording/Playback Area */}
      <div className="flex flex-col items-center justify-center py-6 space-y-4">
        {!audioBlob ? (
          <>
            {/* Recording Button */}
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isUploading}
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-all transform hover:scale-105 active:scale-95 shadow-lg ${
                isRecording
                  ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                  : 'bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
              }`}
            >
              <svg
                className="w-12 h-12 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
              </svg>
            </button>

            {/* Timer */}
            {isRecording && (
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 font-mono">
                  {recordingTime}s
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {MAX_RECORDING_TIME - recordingTime}s remaining
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Audio Playback */}
            <div className="w-full max-w-md">
              <audio
                src={URL.createObjectURL(audioBlob)}
                controls
                className="w-full"
              />
              <div className="text-center text-sm text-gray-600 mt-2">
                Duration: {recordingTime} seconds
              </div>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-center flex-wrap">
        {audioBlob ? (
          <>
            <button
              onClick={uploadAudio}
              disabled={isUploading}
              className="btn-primary px-6 py-3 text-sm disabled:opacity-50"
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                '✓ Save Message'
              )}
            </button>
            <button
              onClick={discardRecording}
              disabled={isUploading}
              className="btn-secondary px-6 py-3 text-sm"
            >
              🔄 Record Again
            </button>
            <button
              onClick={handleCancel}
              disabled={isUploading}
              className="btn-ghost px-6 py-3 text-sm"
            >
              Close
            </button>
          </>
        ) : (
          <button
            onClick={handleCancel}
            disabled={isRecording}
            className="btn-ghost px-6 py-3 text-sm"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

